import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  ArrowBack,
  Delete,
  Add,
  Remove,
  ShoppingCart,
  Payment
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { orderService } from '../../services/orderService';
import { paymentService } from '../../services/paymentService';

const CartPage = () => {
  const { user } = useContext(AuthContext);
  const { items: cartItems, updateQuantity, removeFromCart, getTotalPrice, getItemsByPharmacy, clearCart } = useCart();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);

  const [deliveryAddress, setDeliveryAddress] = useState({
    street: '',
    city: '',
    state: '',
    country: 'Nigeria'
  });

  // 'bank-transfer' | 'paystack' (online card via Paystack)
  const [paymentMethod, setPaymentMethod] = useState('bank-transfer');

  // Load user's address if available
  useEffect(() => {
    if (user?.address) {
      setDeliveryAddress({
        street: user.address.street || '',
        city: user.address.city || '',
        state: user.address.state || '',
        country: user.address.country || 'Nigeria'
      });
    }
  }, [user]);

  const handleUpdateQuantity = (productId, pharmacyId, newQuantity) => {
    updateQuantity(productId, pharmacyId, newQuantity);
  };

  const handleRemoveItem = (productId, pharmacyId) => {
    removeFromCart(productId, pharmacyId);
  };

  const calculateSubtotal = () => {
    return getTotalPrice();
  };

  const calculateDeliveryFee = () => {
    return 250; // Fixed delivery fee for Nigeria
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateDeliveryFee();
  };

  const handleCheckout = async () => {
    if (!user) {
      setMessage('Please login to place an order');
      setShowSnackbar(true);
      return;
    }

    if (cartItems.length === 0) {
      setMessage('Your cart is empty');
      setShowSnackbar(true);
      return;
    }

    setLoading(true);
    try {
      // Get items grouped by pharmacy
      const pharmacyGroups = getItemsByPharmacy();

      if (paymentMethod === 'bank-transfer') {
        // Existing flow: create orders for each pharmacy, paid via bank transfer
        const orderPromises = pharmacyGroups.map(pharmacyGroup => {
          const items = pharmacyGroup.items.map(item => ({
            productId: item._id,
            quantity: item.quantity,
            price: item.price?.nigeria || item.price
          }));
          
          return orderService.createOrder({
            pharmacyId: pharmacyGroup.pharmacyId,
            items,
            deliveryAddress: deliveryAddress,
            paymentMethod: 'bank-transfer',
            notes: 'Please deliver as soon as possible'
          });
        });

        await Promise.all(orderPromises);
        
        setMessage('Orders placed successfully!');
        setShowSnackbar(true);
        clearCart();
        navigate('/orders');
      } else if (paymentMethod === 'paystack') {
        // Online card payment via Paystack for ALL pharmacies in the cart
        // 1) Create orders for each pharmacy with payment method "card"
        const createdOrders = [];

        for (const pharmacyGroup of pharmacyGroups) {
          const items = pharmacyGroup.items.map(item => ({
            productId: item._id,
            quantity: item.quantity,
            price: item.price?.nigeria || item.price
          }));

          // eslint-disable-next-line no-await-in-loop
          const order = await orderService.createOrder({
            pharmacyId: pharmacyGroup.pharmacyId,
            items,
            deliveryAddress: deliveryAddress,
            paymentMethod: 'card',
            notes: 'Please deliver as soon as possible'
          });

          createdOrders.push(order);
        }

        const orderIds = createdOrders.map(o => o._id || o.id);

        // 2) Initialize a single Paystack payment for all orders
        const { authorizationUrl } = await paymentService.initializePaystackForOrders(orderIds);

        // 3) Redirect user to Paystack hosted payment page
        window.location.href = authorizationUrl;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to place order. Please try again.';
      setMessage(errorMessage);
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', color: 'text.primary' }}>
          <Toolbar>
            <IconButton color="inherit" onClick={() => navigate('/customer/dashboard')}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'primary.main', fontWeight: 'bold' }}>
              Shopping Cart
            </Typography>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Box sx={{ textAlign: 'center' }}>
            <ShoppingCart sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Your cart is empty
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Add some products to get started
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/products')}
            >
              Browse Products
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', color: 'text.primary' }}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/customer/dashboard')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'primary.main', fontWeight: 'bold' }}>
            Shopping Cart ({cartItems.length})
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Grid container spacing={4}>
          {/* Cart Items */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Cart Items
                </Typography>
                
                {cartItems.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Your cart is empty
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Add some products to get started
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/products')}
                      startIcon={<ShoppingCart />}
                    >
                      Browse Products
                    </Button>
                  </Box>
                ) : (
                  <List>
                    {cartItems.map((item, index) => (
                    <React.Fragment key={item._id || item.id}>
                      <ListItem>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Box sx={{ width: 80, height: 80, mr: 2 }}>
                            <img
                              src={item.images?.[0] ? `http://localhost:5001/${item.images[0]}` : item.image || '/api/placeholder/100/100'}
                              alt={item.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
                            />
                          </Box>
                          
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" gutterBottom>
                              {item.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {item.pharmacyId?.pharmacyName || item.pharmacy?.name || item.pharmacy || 'Unknown Pharmacy'}
                            </Typography>
                            <Typography variant="h6" color="primary.main">
                              ₦{(item.price?.nigeria || item.price || 0).toLocaleString()}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateQuantity(item._id, item.pharmacyId?._id || item.pharmacyId, item.quantity - 1)}
                            >
                              <Remove />
                            </IconButton>
                            <Typography variant="h6" sx={{ minWidth: 30, textAlign: 'center' }}>
                              {item.quantity}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateQuantity(item._id, item.pharmacyId?._id || item.pharmacyId, item.quantity + 1)}
                            >
                              <Add />
                            </IconButton>
                          </Box>
                          
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveItem(item._id, item.pharmacyId?._id || item.pharmacyId)}
                            sx={{ ml: 2 }}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </ListItem>
                      {index < cartItems.length - 1 && <Divider />}
                    </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Order Summary */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ position: 'sticky', top: 20 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Order Summary
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Subtotal</Typography>
                    <Typography variant="body2">₦{calculateSubtotal().toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Delivery Fee</Typography>
                    <Typography variant="body2">₦{calculateDeliveryFee().toLocaleString()}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight="bold">Total</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      ₦{calculateTotal().toLocaleString()}
                    </Typography>
                  </Box>
                </Box>

                {/* Delivery Address */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Delivery Address
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {deliveryAddress.street}<br />
                    {deliveryAddress.city}, {deliveryAddress.state}<br />
                    {deliveryAddress.country}
                  </Typography>
                  <Button size="small" sx={{ mt: 1 }} onClick={() => setAddressDialogOpen(true)}>
                    Change Address
                  </Button>
                </Box>

                {/* Payment Method */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Payment Method
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={paymentMethod}
                      label="Payment Method"
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      <MenuItem value="bank-transfer">Bank Transfer / Upload Receipt</MenuItem>
                      <MenuItem value="paystack">Card Payment (Paystack)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {paymentMethod === 'bank-transfer' && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      You can upload payment receipt after placing your order.
                    </Typography>
                  </Alert>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleCheckout}
                  disabled={loading}
                  startIcon={<Payment />}
                  sx={{ py: 1.5 }}
                >
                  {loading ? 'Processing...' : 'Proceed to Checkout'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={message.includes('successfully') ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>

      {/* Address Dialog */}
      <Dialog open={addressDialogOpen} onClose={() => setAddressDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Delivery Address</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Street Address"
              value={deliveryAddress.street}
              onChange={(e) => setDeliveryAddress(prev => ({ ...prev, street: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label="City"
              value={deliveryAddress.city}
              onChange={(e) => setDeliveryAddress(prev => ({ ...prev, city: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label="State"
              value={deliveryAddress.state}
              onChange={(e) => setDeliveryAddress(prev => ({ ...prev, state: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Country"
              value={deliveryAddress.country}
              onChange={(e) => setDeliveryAddress(prev => ({ ...prev, country: e.target.value }))}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => setAddressDialogOpen(false)} variant="contained">
            Save Address
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CartPage;
